import React, { cloneElement, useCallback } from 'react';
import { Caption, H4, LabelQldDS } from '@components';
import { FormLabels, generateFieldId, isRequired as isRequiredField } from '@utils';
import {
	Control,
	Controller,
	ControllerRenderProps,
	FieldPath,
	FieldValues,
} from 'react-hook-form';
import * as Yup from 'yup';

import styles from './controllerPlus.module.scss';

interface ChildComponentProps {
	options?: Array<{ value: string; label: string }>;
	fieldName?: string;
	value?: unknown;
	defaultValue?: unknown;
	handleChange?: (...args: unknown[]) => void;
	onChange?: (...args: unknown[]) => void;
	errorMsg?: string;
	disabled?: boolean;
	accessibilityProps?: Record<string, unknown>;
	[key: string]: unknown;
}

interface ControllerPlusProps<T extends FieldValues> {
	control: Control<T>;
	name: string;
	schema: Yup.AnyObject;
	labels: FormLabels<T>;
	shortLabels?: FormLabels<T>;
	formValues: FieldValues;
	children: React.ReactElement<ChildComponentProps>;
	formContext?: unknown;
	disabled?: boolean;
	onChange?: (event: unknown, field: ControllerRenderProps<T, FieldPath<T>>) => void;
	isEditing?: boolean;
	hideLabel?: boolean;
	hideRequired?: boolean;
}

export const ControllerPlus = <T extends FieldValues,>({
	control,
	name,
	schema,
	labels,
	shortLabels,
	formValues,
	children,
	formContext,
	disabled,
	onChange,
	isEditing = true,
	hideLabel = false,
	hideRequired = false,
}: ControllerPlusProps<T>) => {
	const extractLastFieldName = (name: string) => {
		return name.toString().split('.').pop() || name;
	};

	const valueOnReadMode = useCallback(
		(value: string | undefined) => {
			let result = value;
			type Option = { value: string; label: string };
			if (!isEditing && children.props.options) {
				const valueOption = (children.props.options as Option[]).find(
					option => option.value == value,
				);
				result = valueOption?.label ?? value;
			}
			return result;
		},
		[isEditing, children.props.options],
	);

	return (
		<Controller
			control={control}
			name={name as FieldPath<T>}
			render={({ field, fieldState }) => {
				const isRequired = isRequiredField(field.name as keyof T, schema, formValues, formContext);
				const fieldId = generateFieldId(field.name);
				const labelKeyName = extractLastFieldName(field.name);
				const isDisabled = field.disabled || disabled;

				/**
				 * Allow overriding the default onChange handler
				 * @param event
				 */
				const handleChange = (event: unknown) => {
					if (onChange) {
						onChange(event, field);
					} else {
						field.onChange(event);
					}
				};

				return (
					<div className={styles.container}>
						<div className={styles.labelContainer}>
							{hideLabel ? (
								<></>
							) : isEditing ? (
								<LabelQldDS isRequired={isRequired} hideRequired={hideRequired} for={fieldId}>
									{labels[labelKeyName as keyof T]}
								</LabelQldDS>
							) : (
								<Caption className={styles.readOnlyLabel}>
									{shortLabels
										? shortLabels[labelKeyName as keyof T]
										: labels[labelKeyName as keyof T]}
								</Caption>
							)}
						</div>
						{isEditing ? (
							cloneElement(children, {
								fieldName: field.name,
								value: field.value,
								defaultValue: field.value,
								handleChange: handleChange,
								onChange: handleChange,
								errorMsg: fieldState.error?.message,
								disabled: isDisabled,
								accessibilityProps: {
									id: fieldId,
									name: field.name,
									required: isRequired,
									disabled: isDisabled,
								},
							})
						) : (
							<H4 className={styles.value}>{valueOnReadMode(field.value)}</H4>
						)}
					</div>
				);
			}}
		/>
	);
};
